import * as React from "react";
import LazyLoad from "react-lazyload";
import { inject } from "mobx-react";
import EditorialBase from "components/flexComponents/Content/factory/EditorialBase";
import { UitkCardAloha, UitkPlaceholderCard } from "uitk-react-cards";
import { UitkText, UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { ContentFlexComponent, ContentProps } from "components/flexComponents/Content/typings";
import { Media } from "src/typings/microserviceModels/content-flex-module";
import { decorateEditorialHtml } from "components/flexComponents/Content/ContentUtilities";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-experimental-images";
import { ThumborStore } from "src/stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import GalleryCarousel from "src/components/shared/Gallery/GalleryCarousel/GalleryCarousel";
import { GalleryImage } from "src/components/shared/Gallery/typings";
import { UitkScrimType, UitkScrim } from "uitk-react-scrim";
import { UitkPrimaryButton } from "uitk-react-button";

const VideoDisplay = ({ id, video }: { id: string; video: Media }) => (
  <LazyLoad offset={500} height="100%" once={true} placeholder={<UitkPlaceholderCard data-testid="editorial-video" />}>
    <UitkLayoutPosition type="relative" className="videoContainer" tag="div">
      <UitkLayoutPosition type="absolute" position={{ top: "zero", left: "zero" }} cloneElement={true}>
        <iframe
          id={`${id}-video`}
          title={video.mediaAlt}
          src={video.mediaUrl}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
          height="100%"
          width="100%"
        />
      </UitkLayoutPosition>
    </UitkLayoutPosition>
  </LazyLoad>
);

const ImageDisplay = inject("thumbor")(
  ({ image, context, thumbor }: { image: Media; context: ExtendedContextStore; thumbor?: ThumborStore }) => {
    if (!thumbor) {
      return null;
    }

    const width = context?.deviceInformation?.mobile ? 360 : 1040;

    const resizedImage = thumbor?.safelyGenerateThumborizedImageUrl(image.mediaUrl, width, undefined, context);

    return (
      <LazyLoad offset={500} height="100%" once={true} placeholder={<UitkPlaceholderCard />}>
        <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
          <UitkImage alt={image.mediaAlt} src={resizedImage} />
          {image.mediaCaption && (
            <UitkScrim type={UitkScrimType.BOTTOM}>
              <UitkText>{image.mediaCaption}</UitkText>
            </UitkScrim>
          )}
        </UitkFigure>
      </LazyLoad>
    );
  }
);

const MediaCarousel = ({ id, media, title }: { id: string; media: Media[]; title: string }) => {
  const convertedImages = media.reduce((acc: GalleryImage[], item) => {
    if (item.mediaType === "Image") {
      const { mediaUrl, mediaAlt, mediaCaption } = item;
      acc.push({
        alt: mediaAlt,
        src: mediaUrl,
        thumbnailSrc: mediaUrl,
        caption: mediaCaption,
      });
    }

    return acc;
  }, []);

  return (
    <GalleryCarousel
      analyticsId={`${id}.GalleryCarousel`}
      analyticsModule="Editorial"
      title={title}
      images={convertedImages}
    />
  );
};

export class HalfMediaEditorial extends EditorialBase {
  public constructor(editorial: ContentFlexComponent) {
    super(editorial);
  }

  /**
   * An Editorial consists of HTML strings that we parse through with the best of
   * our abilities and display as is on the page. Mainly, we do not control the
   * content of an Editorial, we simply put them in the page.
   */
  public generate(props: ContentProps) {
    const { context } = props;
    const { cardLinkLabel, cardLinkUrl, title: moduleTitle, subtitle: moduleSubtitle } = this.editorial;
    const { id, text, title, media, subtitle } = this.editorial.items[0];

    if (!media || media.length === 0) {
      return null;
    }

    return (
      <UitkSpacing padding={{ block: "three" }}>
        <div
          id={this.flexComponent.id}
          data-fm={this.flexComponent.fmId}
          data-fm-title-id={this.flexComponent.fmTitleId}
          className="HalfMediaEditorial all-cell-1-1 uitk-cell"
        >
          <UitkCardAloha border={true}>
            <div>
              <UitkLayoutGrid columns={{ small: 1, medium: 2 }} justifyContent="space-between">
                <UitkLayoutGridItem>
                  <div>
                    {media.length > 1 && <MediaCarousel id={id} media={media} title={title || ""} />}
                    {media.length === 1 && (
                      <>
                        {media[0].mediaType === "Image" ? (
                          <ImageDisplay image={media[0]} context={context} />
                        ) : (
                          <VideoDisplay id={id} video={media[0]} />
                        )}
                      </>
                    )}
                  </div>
                </UitkLayoutGridItem>
                <UitkLayoutGridItem>
                  <div>
                    <UitkSpacing padding={{ block: "three", inline: "four" }}>
                      <div>
                        {moduleTitle && (
                          <UitkSpacing margin={{ blockend: "two" }}>
                            <div>
                              <UitkHeading level={2} size={500}>
                                {moduleTitle}
                              </UitkHeading>
                              {moduleSubtitle && <UitkSubheading>{moduleSubtitle}</UitkSubheading>}
                            </div>
                          </UitkSpacing>
                        )}
                        {title && (
                          <div>
                            <UitkHeading level={moduleTitle ? 3 : 2} size={500}>
                              {title}
                            </UitkHeading>
                            {subtitle && <UitkSubheading>{subtitle}</UitkSubheading>}
                          </div>
                        )}
                        <UitkSpacing margin={{ blockstart: "two", blockend: "four" }}>
                          <UitkText size={300}>
                            <div dangerouslySetInnerHTML={decorateEditorialHtml(text)} />
                          </UitkText>
                        </UitkSpacing>
                        {cardLinkUrl && cardLinkLabel && <UitkPrimaryButton text={cardLinkLabel} href={cardLinkUrl} />}
                      </div>
                    </UitkSpacing>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkCardAloha>
        </div>
      </UitkSpacing>
    );
  }
}

export default HalfMediaEditorial;
